import React from "react";
import { Card, CardContent } from "@heroui/react";
import { Icon } from "@iconify/react/dist/iconify.js";

import { useBrandAccent } from "@/hooks/useBrandAccent";

interface WorkflowDescriptionBubbleProps {
  name: string;
  description: string;
}

/**
 * Displays workflow name and description in an info bubble.
 * Only shown for specialised workflows (not chat_agent) before any messages are sent.
 *
 * Both the surface and the heading colour come from the active tenant pack rather than
 * a fixed palette, so every brand gets the equivalent treatment from its own values.
 * The card takes `bg-content1`, which resolves through `--surface` to white in light
 * mode and to the tenant's dark surface (LSEG Midnight) in dark mode, and the grey
 * surround is the brand-neutral `default` scale.
 *
 * The description body deliberately stays on the neutral scale, because a paragraph of
 * body copy in any accent is harder to read than the same paragraph in the text role
 * even where the ratio passes. The heading and icon do take the accent, but through
 * `useBrandAccent`, which corrects it where the tenant's own value would fail WCAG AA
 * against that tenant's surface. Length is not what makes a string safe to colour, so
 * an earlier version of this comment claiming the heading was "short enough to carry
 * it" was wrong; see `src/utils/accessibleAccent.ts` for the measured figures. See
 * ADR-0017 for how tenant colours reach the app.
 */
export const WorkflowDescriptionBubble: React.FC<WorkflowDescriptionBubbleProps> = ({
  name,
  description,
}) => {
  // The tenant's accent role, corrected for legibility on that tenant's own surface.
  // For LSEG this passes untouched, so the heading takes the exact platform blue the
  // rest of the app uses rather than an approximation of it. Published as a custom
  // property and consumed by both children.
  const accent = useBrandAccent();
  const accentStyle = { "--workflow-description-accent": accent } as React.CSSProperties;

  return (
    <div className="px-4 py-2" data-testid="workflow-description-bubble">
      <Card className="bg-content1 border border-default-200" style={accentStyle}>
        <CardContent className="py-3 px-4">
          <div className="flex items-start gap-2">
            <Icon
              className="text-(--workflow-description-accent) mt-0.5 flex-shrink-0"
              icon="solar:info-circle-bold"
              width={16}
            />
            <div className="text-sm">
              <p className="font-medium mb-1 text-(--workflow-description-accent)">{name}</p>
              <p className="text-default-700 whitespace-pre-line">{description}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
